import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Copy, RotateCw, Save, Zap, TrendingUp, TrendingDown, Users, FileText, Mic, AlertTriangle, Mail, Send, RefreshCw, Sparkles, X, Check } from "lucide-react";

export default function OrgSettings() {
  const [org, setOrg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const reload = () => {
    api.get("/settings/org").then(({data}) => setOrg(data)).catch(() => {});
    api.get("/settings/org/usage").then(({data}) => setUsage(data)).catch(() => {});
  };

  useEffect(() => { reload(); }, []);
  if (!org) return <div className="p-8">Loading…</div>;

  const set = (k, v) => setOrg({ ...org, [k]: v });
  const setOkta = (k, v) => setOrg({ ...org, okta: { ...(org.okta || {}), [k]: v } });
  const save = async () => {
    try {
      const okta = org.okta || {};
      const payload = {
        name: org.name, industry: org.industry, logo_url: org.logo_url,
        webhook_url: org.webhook_url, data_retention_days: org.data_retention_days,
        sso_enabled: org.sso_enabled, sso_provider: org.sso_provider,
        sso_mode: org.sso_mode || "simulated",
        sso_email_domain: org.sso_email_domain, sso_entity_id: org.sso_entity_id,
        sso_idp_url: org.sso_idp_url, sso_default_role: org.sso_default_role,
        okta_issuer: okta.issuer ?? null,
        okta_client_id: okta.client_id ?? null,
      };
      // Only send a client_secret value if the admin typed a new one in this session
      if (okta._secret_input) payload.okta_client_secret = okta._secret_input;
      const { data } = await api.put("/settings/org", payload);
      setOrg(data); toast.success("Organisation saved");
    } catch { toast.error("Save failed"); }
  };
  const rotateKey = async () => {
    if (!confirm("Rotate API key? Existing integrations will stop working.")) return;
    const { data } = await api.post("/settings/org/api-key/rotate");
    setOrg({ ...org, api_key: data.api_key });
    toast.success("API key rotated");
  };
  const testWebhook = async () => {
    setWebhookBusy(true);
    try {
      const { data } = await api.post("/settings/org/webhook/test");
      if (data.delivered) toast.success(`Webhook delivered · HTTP ${data.status_code}`);
      else toast.error(`Webhook failed · ${data.status_code || ""} ${data.error || data.reason || ""}`.trim());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Webhook test failed");
    } finally { setWebhookBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(org.api_key); toast.success("Copied"); };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="font-serif text-3xl mb-1">Organization Settings</h1>
          <p className="text-sm text-[var(--aria-text-muted)]">Profile, API access, retention and SSO.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="aria-card p-6 space-y-4">
            <h3 className="font-serif text-lg">Organisation profile</h3>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Name</label>
              <input value={org.name} onChange={e=>set("name", e.target.value)} data-testid="org-name"
                className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Industry</label>
              <select value={org.industry} onChange={e=>set("industry", e.target.value)} data-testid="org-industry"
                className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                {["Biotech","Pharma","Clinical","Research"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Logo URL</label>
              <input value={org.logo_url || ""} onChange={e=>set("logo_url", e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Data retention (days)</label>
              <input type="number" min="7" value={org.data_retention_days} onChange={e=>set("data_retention_days", parseInt(e.target.value))}
                data-testid="org-retention"
                className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
            </div>
          </div>

          <div className="aria-card p-6 space-y-4">
            <h3 className="font-serif text-lg">API & integrations</h3>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">API key</label>
              <div className="flex gap-2 mt-1">
                <input value={org.api_key} readOnly data-testid="org-api-key"
                  className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono text-xs" />
                <button onClick={copy} data-testid="copy-api-key" className="px-3 rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5"><Copy size={14} /></button>
                <button onClick={rotateKey} data-testid="rotate-api-key" className="px-3 rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5"><RotateCw size={14} /></button>
              </div>
              <p className="text-xs text-[var(--aria-text-muted)] mt-1">
                Pass as <code>x-api-key</code> header for programmatic uploads.
                Also used as the HMAC secret to sign outgoing webhook events —
                <strong className="text-[var(--aria-warning)]"> rotating this key will invalidate webhook signatures</strong> on your receiver.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Webhook URL (conversation events)</label>
              <div className="flex gap-2 mt-1">
                <input value={org.webhook_url || ""} onChange={e=>set("webhook_url", e.target.value)}
                  data-testid="org-webhook"
                  placeholder="https://hooks.yourapp.com/aria"
                  className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
                <button onClick={testWebhook} data-testid="test-webhook-btn"
                  disabled={!org.webhook_url || webhookBusy}
                  className="px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5 disabled:opacity-40 whitespace-nowrap">
                  {webhookBusy ? "Sending…" : "Send test"}
                </button>
              </div>
              <p className="text-xs text-[var(--aria-text-muted)] mt-1">
                Events POSTed with <code>X-ARIA-Signature</code> HMAC header. Save first, then test.
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)] flex items-center justify-between">
                <span>SSO (SAML / OKTA)</span>
                <label className="flex items-center gap-2 text-[10px] normal-case tracking-normal">
                  <input type="checkbox" checked={!!org.sso_enabled}
                    onChange={e=>set("sso_enabled", e.target.checked)}
                    data-testid="sso-enabled"
                    className="accent-[#2563FF]" />
                  Enabled
                </label>
              </div>
              {org.sso_enabled ? (
                <div className="mt-3 space-y-3 p-3 rounded-md border border-[#2563FF]/30 bg-[#2563FF]/[0.04]">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Mode</label>
                      <select value={org.sso_mode || "simulated"} onChange={e=>set("sso_mode", e.target.value)}
                        data-testid="sso-mode"
                        className="w-full mt-1 px-2 py-1.5 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                        <option value="simulated">Simulated (demo)</option>
                        <option value="real_okta">Real Okta (OIDC)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Default role</label>
                      <select value={org.sso_default_role || "scientist"} onChange={e=>set("sso_default_role", e.target.value)}
                        data-testid="sso-default-role"
                        className="w-full mt-1 px-2 py-1.5 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                        <option value="scientist">Scientist</option>
                        <option value="viewer">Viewer</option>
                        <option value="org_admin">Org Admin</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Email domain</label>
                    <input value={org.sso_email_domain || ""} onChange={e=>set("sso_email_domain", e.target.value.toLowerCase())}
                      data-testid="sso-domain"
                      placeholder="biotechlabs.com"
                      className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono" />
                    <p className="text-[10px] text-[var(--aria-text-muted)] mt-1">Users with this email domain will be routed to your IdP.</p>
                  </div>

                  {(org.sso_mode || "simulated") === "real_okta" ? (
                    <div className="space-y-3 border-t border-[var(--aria-border)] pt-3" data-testid="okta-config">
                      <div className="text-[11px] uppercase tracking-wider text-[#2563FF] font-medium">Okta OIDC application</div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Issuer URL</label>
                        <input value={(org.okta && org.okta.issuer) || ""}
                          onChange={e=>setOkta("issuer", e.target.value.trim())}
                          data-testid="okta-issuer"
                          placeholder="https://your-org.okta.com/oauth2/default"
                          className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Client ID</label>
                        <input value={(org.okta && org.okta.client_id) || ""}
                          onChange={e=>setOkta("client_id", e.target.value.trim())}
                          data-testid="okta-client-id"
                          placeholder="0oa1abcdEFGHijklm5d6"
                          className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Client secret</label>
                        <input type="password"
                          value={(org.okta && org.okta._secret_input) || ""}
                          onChange={e=>setOkta("_secret_input", e.target.value)}
                          data-testid="okta-client-secret"
                          placeholder={(org.okta && org.okta.client_secret_set) ? "•••••• stored — type to rotate" : "Paste Okta client secret"}
                          className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono" />
                        <p className="text-[10px] text-[var(--aria-text-muted)] mt-1">Stored encrypted at rest. Leave blank to keep the current secret.</p>
                      </div>
                      <div className="text-[10px] text-[var(--aria-text-muted)] bg-[var(--aria-bg)] p-2 rounded space-y-1">
                        <div><strong className="text-[#2563FF]">Sign-in redirect URI:</strong> {window.location.origin}/api/sso/okta/callback</div>
                        <div><strong className="text-[#2563FF]">Scopes:</strong> openid profile email</div>
                        <div>Add the redirect URI above to your Okta OIDC web app, then enter the issuer, client ID and secret here.</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">IdP URL (simulated)</label>
                      <input value={org.sso_idp_url || ""} onChange={e=>set("sso_idp_url", e.target.value)}
                        data-testid="sso-idp-url"
                        placeholder="/sso-idp (demo)"
                        className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] font-mono" />
                      <p className="text-[10px] text-[var(--aria-text-muted)] mt-1">Leave default to use the bundled simulated IdP page.</p>
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[var(--aria-text-muted)]">Provider label</label>
                    <select value={org.sso_provider || "okta"} onChange={e=>set("sso_provider", e.target.value)}
                      data-testid="sso-provider"
                      className="w-full mt-1 px-2 py-1.5 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)]">
                      <option value="okta">OKTA</option>
                      <option value="saml">SAML 2.0</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="mt-2 p-3 rounded-md border border-dashed border-[var(--aria-border)] text-xs text-[var(--aria-text-muted)]">
                  Enable SSO to let your team sign in with OKTA or SAML 2.0. Users will be auto-provisioned on first sign-in.
                </div>
              )}
            </div>
          </div>
        </div>

        <UsageWidget org={org} usage={usage} onUpgrade={() => setUpgradeOpen(true)} />

        <DigestWidget org={org} setOrg={setOrg} />

        <div className="flex justify-end">
          <button onClick={save} data-testid="save-org" className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] font-medium">
            <Save size={16} /> Save changes
          </button>
        </div>

        <FeatureFlagsPanel />
      </div>
      {upgradeOpen && <UpgradeModal currentPlan={org.plan} orgName={org.name} onClose={() => setUpgradeOpen(false)} />}
    </div>
  );
}


function FeatureFlagsPanel() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/settings/org/feature-flags"); setFlags(data.flags); }
    catch (err) { console.error("[ARIA]", err); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, value) => {
    setBusy(key);
    setFlags(prev => prev.map(f => f.key === key ? { ...f, value } : f));
    try {
      const { data } = await api.put("/settings/org/feature-flags", { flags: { [key]: value } });
      setFlags(data.flags);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update flag");
      load();
    } finally { setBusy(null); }
  };

  if (!flags) {
    return (
      <div className="aria-card p-6" data-testid="feature-flags-panel">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-serif text-lg">Feature flags</h3>
          <span className="aria-pill">loading…</span>
        </div>
        <p className="text-xs text-[var(--aria-text-muted)] mt-2">Loading organisation feature flags…</p>
      </div>
    );
  }

  const groups = {
    voice:     { label: "Voice & conversation", icon: Mic,    color: "text-[var(--aria-accent)]" },
    chat:      { label: "Chat",                 icon: Zap,    color: "text-[var(--aria-primary)]" },
    documents: { label: "Documents",            icon: FileText,color: "text-[var(--aria-primary)]" },
    ops:       { label: "Operations",           icon: Sparkles,color: "text-[var(--aria-primary)]" },
  };

  return (
    <div className="aria-card p-6" data-testid="feature-flags-panel">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="font-serif text-lg">Feature flags</h3>
        <span className="aria-pill">Org-scoped · live</span>
      </div>
      <p className="text-xs text-[var(--aria-text-muted)] mb-5">
        Tune which ARIA capabilities are exposed to your scientists. Changes take effect immediately for everyone in this organisation.
      </p>

      <div className="space-y-6">
        {Object.entries(groups).map(([gk, g]) => {
          const items = flags.filter(f => f.group === gk);
          if (!items.length) return null;
          const GIcon = g.icon;
          return (
            <div key={gk}>
              <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-medium mb-3 ${g.color}`}>
                <GIcon size={13} /> {g.label}
              </div>
              <div className="space-y-2">
                {items.map(f => (
                  <FlagRow key={f.key} flag={f} busy={busy === f.key} onToggle={(v) => toggle(f.key, v)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FlagRow({ flag, busy, onToggle }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 px-3 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-border-strong)] transition-colors" data-testid={`flag-row-${flag.key}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{flag.label}</div>
        <div className="text-xs text-[var(--aria-text-muted)] mt-0.5 leading-relaxed">{flag.description}</div>
      </div>
      <button
        onClick={() => onToggle(!flag.value)}
        disabled={busy}
        data-testid={`flag-toggle-${flag.key}`}
        aria-pressed={flag.value}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
          flag.value ? "bg-[var(--aria-primary)]" : "bg-[var(--aria-border-strong)]"
        }`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          flag.value ? "translate-x-5" : "translate-x-0.5"
        }`} />
      </button>
    </div>
  );
}


function UsageWidget({ org, usage, onUpgrade }) {
  if (!usage) {
    return (
      <div className="aria-card p-6" data-testid="usage-widget-loading">
        <h3 className="font-serif text-lg mb-3">Plan & usage</h3>
        <div className="text-sm text-[var(--aria-text-muted)]">Loading usage…</div>
      </div>
    );
  }
  const { plan, month_label, questions_month, questions_last_month, delta_pct,
    quota, percent_used, voice_minutes_estimate, documents, active_users_month,
    daily_series, top_users } = usage;
  const approaching = percent_used !== null && percent_used >= 80;
  const over = percent_used !== null && percent_used >= 100;
  const max = Math.max(1, ...daily_series.map(d => d.count));
  const accent = over ? "#B23A48" : approaching ? "#F59E0B" : "#2563FF";

  return (
    <div className="aria-card p-6 space-y-6" data-testid="usage-widget">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg">Plan & usage</h3>
          <p className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mt-0.5">
            {month_label} · {plan} plan
          </p>
        </div>
        {delta_pct !== null && (
          <div className={`flex items-center gap-1 text-sm ${delta_pct >= 0 ? "text-[#2563FF]" : "text-[var(--aria-text-muted)]"}`} data-testid="usage-delta">
            {delta_pct >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
            <span>{Math.abs(delta_pct)}% vs last month</span>
          </div>
        )}
      </div>

      {/* Quota bar */}
      <div data-testid="usage-quota">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-sm text-[var(--aria-text-muted)]">Questions this month</div>
          <div className="font-serif text-2xl">
            {questions_month.toLocaleString()}
            {quota && <span className="text-sm text-[var(--aria-text-muted)] font-sans"> / {quota.toLocaleString()}</span>}
          </div>
        </div>
        {quota ? (
          <>
            <div className="h-2 rounded-full bg-[var(--aria-border)] overflow-hidden">
              <div className="h-full transition-all"
                style={{ width: `${Math.min(100, percent_used ?? 0)}%`, background: accent }} />
            </div>
            <div className="flex justify-between mt-1 text-xs text-[var(--aria-text-muted)]">
              <span>{percent_used ?? 0}% used</span>
              <span>{Math.max(0, quota - questions_month).toLocaleString()} remaining</span>
            </div>
          </>
        ) : (
          <div className="text-xs text-[var(--aria-text-muted)]">Unlimited on {plan} plan.</div>
        )}
        {approaching && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-md border border-[var(--aria-warning)]/40 bg-[var(--aria-warning)]/5 text-sm"
            data-testid="usage-upgrade-nudge">
            <AlertTriangle size={16} className="mt-0.5 text-[var(--aria-warning)] shrink-0"/>
            <div className="flex-1">
              {over ? "You've hit your monthly quota." : `You've used ${percent_used}% of your monthly quota.`}
              {" "}<button onClick={onUpgrade} className="underline font-medium" data-testid="upgrade-cta-nudge">
                Upgrade your plan
              </button> to unlock more capacity.
            </div>
          </div>
        )}
        {/* Always-visible upgrade button for non-enterprise plans */}
        {quota && !approaching && (
          <div className="mt-3 flex justify-end">
            <button onClick={onUpgrade} data-testid="upgrade-cta"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-[#2563FF] text-[#2563FF] hover:bg-[#2563FF] hover:text-white transition-colors">
              <Sparkles size={14}/> Upgrade plan
            </button>
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="usage-stats">
        <StatCard icon={<Mic size={14}/>} label="Voice minutes" value={voice_minutes_estimate} suffix="min" hint="TTS + STT this month"/>
        <StatCard icon={<Users size={14}/>} label="Active users" value={active_users_month} hint="this month"/>
        <StatCard icon={<FileText size={14}/>} label="Documents" value={documents} hint="indexed"/>
      </div>

      {/* Sparkline */}
      <div data-testid="usage-sparkline">
        <div className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mb-2">Last 14 days</div>
        <div className="flex items-end gap-1 h-16">
          {daily_series.map((d, i) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="w-full rounded-sm transition-colors"
                style={{ height: `${(d.count / max) * 56 + 2}px`, background: accent, opacity: 0.35 + (d.count / max) * 0.65 }}
                title={`${d.date}: ${d.count}`}/>
              {i % 2 === 0 && <span className="text-[9px] text-[var(--aria-text-muted)]">{d.date.slice(3)}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Top users */}
      {top_users.length > 0 && (
        <div data-testid="usage-top-users">
          <div className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mb-2">Top users this month</div>
          <div className="divide-y divide-[var(--aria-border)]">
            {top_users.map((u, i) => (
              <div key={u.email} className="flex items-center justify-between py-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="w-5 text-xs text-[var(--aria-text-muted)] tabular-nums">{i + 1}.</span>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-[var(--aria-text-muted)]">{u.email} · {u.role}</div>
                  </div>
                </div>
                <div className="tabular-nums">{u.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-[var(--aria-border)] grid sm:grid-cols-3 gap-4 text-sm">
        <div><div className="text-xs uppercase text-[var(--aria-text-muted)] tracking-wider">Plan</div><div className="font-serif text-xl mt-1 capitalize">{org.plan}</div></div>
        <div><div className="text-xs uppercase text-[var(--aria-text-muted)] tracking-wider">Created</div><div className="mt-1">{new Date(org.created_at).toLocaleDateString()}</div></div>
        <div><div className="text-xs uppercase text-[var(--aria-text-muted)] tracking-wider">Accent</div><div className="mt-1 flex items-center gap-2"><span className="w-4 h-4 rounded-full" style={{background:org.accent_color}}/> {org.accent_color}</div></div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, suffix, hint }) {
  return (
    <div className="rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg-subtle,transparent)] p-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">
        {icon}<span>{label}</span>
      </div>
      <div className="font-serif text-2xl mt-1">{value}{suffix && <span className="text-sm text-[var(--aria-text-muted)] font-sans ml-1">{suffix}</span>}</div>
      {hint && <div className="text-[11px] text-[var(--aria-text-muted)] mt-0.5">{hint}</div>}
    </div>
  );
}

function DigestWidget({ org, setOrg }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/org/digest/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Could not load digest preview");
    } finally { setLoading(false); }
  };

  useEffect(() => { load();   }, []);

  const toggleEnabled = async () => {
    const next = !(org.digest_enabled !== false);
    try {
      const { data } = await api.put("/settings/org", { digest_enabled: next });
      setOrg(data);
      toast.success(`Weekly digest ${next ? "enabled" : "paused"}`);
    } catch { toast.error("Update failed"); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/settings/org/digest/send-test");
      if (data.delivered) toast.success(`Test digest delivered (HTTP ${data.status_code})`);
      else toast.error("Webhook responded but did not return 2xx");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Configure a webhook URL above first");
    } finally { setSending(false); }
  };

  const enabled = org.digest_enabled !== false;
  const hasWebhook = preview?.webhook_configured;

  return (
    <div className="aria-card p-6 space-y-4" data-testid="digest-widget">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#2563FF]"/>
            <h3 className="font-serif text-lg">Weekly digest</h3>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${enabled ? "bg-[#2563FF]/10 text-[#2563FF]" : "bg-[var(--aria-border)] text-[var(--aria-text-muted)]"}`}>
              {enabled ? "Active" : "Paused"}
            </span>
          </div>
          <p className="text-xs text-[var(--aria-text-muted)] mt-1 max-w-xl">
            Every Monday at 09:00 UTC, ARIA fires a <code className="px-1 rounded bg-[var(--aria-border)] text-[10px]">digest.weekly</code> event
            to your webhook with the previous 7 days of activity. Route it to email via Zapier / n8n / Make,
            or consume the JSON directly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="digest-refresh"
            className="p-2 rounded-md border border-[var(--aria-border)] hover:bg-[var(--aria-border)]/30" title="Refresh preview">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          </button>
          <button onClick={toggleEnabled} data-testid="digest-toggle"
            className="px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] hover:bg-[var(--aria-border)]/30">
            {enabled ? "Pause" : "Resume"}
          </button>
          <button onClick={sendTest} disabled={sending || !hasWebhook} data-testid="digest-send-test"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={14}/> {sending ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>

      {!hasWebhook && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[var(--aria-warning)]/40 bg-[var(--aria-warning)]/5 text-sm" data-testid="digest-no-webhook">
          <AlertTriangle size={16} className="mt-0.5 text-[var(--aria-warning)] shrink-0"/>
          <div>Set a <strong>Webhook URL</strong> in the Org info card above to start receiving weekly digests.</div>
        </div>
      )}

      {preview ? (
        <pre className="text-xs font-mono whitespace-pre-wrap p-4 rounded-md bg-[var(--aria-border)]/30 border border-[var(--aria-border)] max-h-72 overflow-auto" data-testid="digest-preview">
{preview.text_preview}
        </pre>
      ) : (
        <div className="text-sm text-[var(--aria-text-muted)]">{loading ? "Loading preview…" : "No preview available."}</div>
      )}
    </div>
  );
}


function UpgradeModal({ currentPlan, orgName, onClose }) {
  const [targetPlan, setTargetPlan] = useState(currentPlan === "starter" ? "pro" : "enterprise");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  // Plans available for request (exclude the current one and anything lower)
  const tiers = [
    { id: "pro", label: "Pro", blurb: "2,500 questions / month, priority support, SLA",
      features: ["2,500 questions / month", "Priority email support", "99.5% uptime SLA"] },
    { id: "enterprise", label: "Enterprise", blurb: "Unlimited usage, SSO, dedicated success manager",
      features: ["Unlimited questions", "Okta / SAML SSO", "Dedicated CSM + onboarding", "Custom data retention"] },
  ].filter(t => t.id !== currentPlan);

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/settings/org/upgrade-request", { target_plan: targetPlan, notes });
      setDone(true);
      toast.success("Request sent — our team will reach out shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="upgrade-modal">
      <div className="aria-card max-w-xl w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" data-testid="upgrade-modal-close"
          className="absolute top-4 right-4 p-1.5 rounded-md hover:bg-[var(--aria-border)]/40">
          <X size={16}/>
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="upgrade-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#2563FF]/10 mb-4">
              <Check size={22} className="text-[#2563FF]"/>
            </div>
            <h2 className="font-serif text-2xl mb-2">Request received</h2>
            <p className="text-sm text-[var(--aria-text-muted)] max-w-sm mx-auto">
              Our team has been notified and will be in touch within one business day to discuss your move to
              the <span className="capitalize font-medium">{targetPlan}</span> plan.
            </p>
            <button onClick={onClose} className="mt-6 px-5 py-2 rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE]" data-testid="upgrade-done">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={18} className="text-[#2563FF]"/>
              <h2 className="font-serif text-2xl">Request an upgrade</h2>
            </div>
            <p className="text-sm text-[var(--aria-text-muted)] mb-5">
              <span className="font-medium">{orgName}</span> is currently on the{" "}
              <span className="capitalize font-medium">{currentPlan}</span> plan. Let our team know which plan
              fits and we'll reach out to finalize.
            </p>

            <div className="space-y-2 mb-4">
              {tiers.map((t) => {
                const selected = targetPlan === t.id;
                return (
                  <button key={t.id} onClick={() => setTargetPlan(t.id)}
                    data-testid={`upgrade-tier-${t.id}`}
                    className={`w-full text-left rounded-lg border p-4 transition-colors ${
                      selected ? "border-[#2563FF] bg-[#2563FF]/5"
                        : "border-[var(--aria-border)] hover:border-[#2563FF]/40"
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-serif text-lg">{t.label}</div>
                        <p className="text-xs text-[var(--aria-text-muted)] mt-0.5">{t.blurb}</p>
                        <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                          {t.features.map(f => (
                            <li key={f} className="flex items-start gap-1.5 text-xs">
                              <Check size={12} className="mt-0.5 text-[#2563FF] shrink-0"/>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={`mt-1 w-4 h-4 rounded-full border-2 shrink-0 ${
                        selected ? "border-[#2563FF] bg-[#2563FF]" : "border-[var(--aria-border)]"
                      }`}/>
                    </div>
                  </button>
                );
              })}
            </div>

            <label className="block text-xs uppercase tracking-wider text-[var(--aria-text-muted)] mb-1">
              Anything we should know? <span className="lowercase">(optional)</span>
            </label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              data-testid="upgrade-notes"
              placeholder="Team size, timeline, specific requirements…"
              className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-transparent text-sm focus:outline-none focus:border-[#2563FF]"/>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} data-testid="upgrade-cancel"
                className="px-4 py-2 text-sm rounded-md border border-[var(--aria-border)] hover:bg-[var(--aria-border)]/30">
                Cancel
              </button>
              <button onClick={submit} disabled={sending} data-testid="upgrade-submit"
                className="flex items-center gap-1.5 px-5 py-2 text-sm rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] disabled:opacity-60">
                <Send size={14}/> {sending ? "Sending…" : "Send request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
